import { Server } from '~/api/models/Server';
import { User } from '~/api/models/User';
import { BaseModel } from './BaseModel';

interface Device {
    userAgent?: string;
    ipAddress?: string;
}

export class AuditLog extends BaseModel {
    public action = '';
    public subaction = '';
    public steamid64: string | null = null;
    public device: Device = {};
    public metadata: Record<any, any> = {};
    public createdAt = '';

    public get user(): User {
        return this.getRelationship('user');
    }

    public get server(): Server | undefined {
        return this.getRelationship('server');
    }

    public get translationKey(): string {
        // OOM kills come through as a regular crash event but warrant their own
        // copy so the user understands it was a memory exhaustion, not a generic crash.
        if (this.subaction === 'crash' && this.metadata?.oomKilled) {
            return 'audits.server.oom';
        }

        return `audits.${this.action.replace(/:/g, '.')}.${this.subaction}`;
    }

    public get iconInfo() {
        if (this.action === 'rust') {
            return ['gamepad', 'bg-[rgba(192,76,46,0.2)]', 'text-[#C04C2E]'];
        }

        // Action-specific icons come first — they take precedence over
        // the subaction fallback so ops like "git commit" get a commit
        // icon instead of a generic one.
        if (this.action === 'server:power') {
            switch (this.subaction) {
                case 'start':
                    return ['play', 'bg-success-500/20', 'text-success-500'];
                case 'stop':
                    return ['stop', 'bg-warning-500/20', 'text-warning-500'];
                case 'restart':
                    return ['rotate-right', 'bg-blue-500/20', 'text-blue-300'];
                case 'terminate':
                    return ['skull', 'bg-danger-500/20', 'text-danger-500'];
                case 'crash':
                    return ['power-off', 'bg-danger-500/20', 'text-danger-500'];
            }
        }

        if (this.action === 'server:git') {
            switch (this.subaction) {
                case 'clone':
                    return ['copy', 'bg-accent-500/20', 'text-accent-500'];
                case 'pull':
                    return ['arrow-down', 'bg-accent-500/20', 'text-accent-500'];
                case 'push':
                    return ['arrow-up', 'bg-accent-500/20', 'text-accent-500'];
                case 'commit':
                    return ['check', 'bg-success-500/20', 'text-success-500'];
                case 'stash':
                    return ['box-archive', 'bg-blue-500/20', 'text-blue-300'];
                case 'revert':
                    return ['rotate-left', 'bg-warning-500/20', 'text-warning-500'];
                case 'checkout':
                    return ['code-branch', 'bg-accent-500/20', 'text-accent-500'];
                case 'discard':
                    return ['trash', 'bg-danger-500/20', 'text-danger-500'];
                case 'pubkey-rotate':
                    return ['key', 'bg-accent-500/20', 'text-accent-500'];
            }
        }

        if (this.action === 'server:git.webhook') {
            // Plug for both create + delete — the colour carries the
            // state difference. `plug-circle-xmark` isn't in the
            // registered FA icon set so we stick with the base icon.
            switch (this.subaction) {
                case 'create':
                    return ['plug', 'bg-success-500/20', 'text-accent-500'];
                case 'delete':
                    return ['plug', 'bg-danger-500/20', 'text-danger-500'];
            }
        }

        if (this.action === 'server:file' && this.subaction === 'upload') {
            return ['upload', 'bg-accent-500/20', 'text-accent-500'];
        }

        if (this.action === 'server:sftp') {
            return ['folder-tree', 'bg-blue-500/20', 'text-blue-300'];
        }

        if (this.action === 'server:ssh') {
            switch (this.subaction) {
                case 'connected':
                    return ['plug', 'bg-success-500/20', 'text-success-500'];
                case 'disconnected':
                    return ['plug', 'bg-danger-500/20', 'text-danger-500'];
            }
        }

        if (this.action === 'server:console' && this.subaction === 'command') {
            return ['terminal', 'bg-blue-500/20', 'text-blue-300'];
        }

        switch (this.subaction) {
            case 'create':
                return ['plus', 'bg-success-500/20', 'text-accent-500'];
            case 'update':
                return ['pencil', 'bg-warning-500/20', 'text-warning-500'];
            case 'delete':
                return ['trash', 'bg-danger-500/20', 'text-danger-500'];
            case 'trigger':
                return ['play-circle', 'bg-blue-500/20', 'text-blue-300'];
            case 'deploy':
                return ['server', 'bg-blue-500/20', 'text-blue-300'];
            case 'download':
                return ['download', 'bg-accent-500/20', 'text-accent-500'];
            case 'restart':
            case 'crash':
                return ['power-off', 'bg-danger-500/20', 'text-danger-500'];

            default:
                return ['question', 'bg-accent-200/20', 'text-accent-500'];
        }
    }
}
